/**
 * One vocabulary for the GitHub-derived signals CHEW describes in prose.
 *
 * Words follow GitHub's own for GitHub's concepts (checks, conversations,
 * changes requested) so reviewers moving between the two never retranslate.
 * Queue surfaces render their own compact flags; this is the sentence form,
 * used in detail panes, tooltips and by screen readers.
 */

export interface PullSignal {
  key: "checks" | "comments";
  full: string;
}

interface ChecksInput {
  state: "success" | "failure" | "pending" | "neutral" | "unavailable";
  total: number;
  failed: number;
  pending: number;
}

interface CommentsInput {
  issueComments: number;
  reviewThreads: number;
  unresolvedThreads: number;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

export function checksSignal(ci: ChecksInput | null | undefined): PullSignal {
  if (!ci || ci.state === "unavailable") return { key: "checks", full: "Check status unavailable" };
  if (ci.state === "failure") {
    return {
      key: "checks",
      full: ci.total > 0 ? `${ci.failed} of ${plural(ci.total, "check", "checks")} failing` : `${plural(ci.failed, "check", "checks")} failing`,
    };
  }
  if (ci.state === "pending") {
    return {
      key: "checks",
      full: ci.total > 0 ? `${ci.pending} of ${plural(ci.total, "check", "checks")} still running` : `${plural(ci.pending, "check", "checks")} still running`,
    };
  }
  if (ci.state === "success") {
    return { key: "checks", full: ci.total > 0 ? `All ${plural(ci.total, "check", "checks")} passing` : "Checks passing" };
  }
  return { key: "checks", full: "No checks configured" };
}

export function commentsSignal(conversation: CommentsInput | null | undefined): PullSignal {
  if (!conversation) return { key: "comments", full: "Comments unavailable" };
  if (conversation.unresolvedThreads > 0) {
    return { key: "comments", full: plural(conversation.unresolvedThreads, "unresolved conversation", "unresolved conversations") };
  }
  const total = conversation.issueComments + conversation.reviewThreads;
  if (total > 0) {
    return {
      key: "comments",
      full: conversation.reviewThreads > 0 ? `${plural(total, "comment", "comments")}, every conversation resolved` : plural(total, "comment", "comments"),
    };
  }
  return { key: "comments", full: "No comments" };
}
